import { useState } from "react";
import type { FeaturePropertyInfo } from "@/hooks/useFeatureProperties";

interface FilterEditorProps {
  value: unknown;
  onChange: (value: unknown) => void;
  /** Available property names and values from live features */
  featureProperties?: FeaturePropertyInfo;
}

interface FilterRow {
  property: string;
  operator: string;
  value: string;
}

const OPERATORS = [
  { value: "==", label: "=" },
  { value: "!=", label: "≠" },
  { value: ">", label: ">" },
  { value: "<", label: "<" },
  { value: ">=", label: "≥" },
  { value: "<=", label: "≤" },
  { value: "has", label: "has" },
  { value: "!has", label: "!has" },
];

/**
 * Visual filter builder. Builds MapLibre filter expressions from rows of
 * property + operator + value, combined with all/any.
 */
export function FilterEditor({ value, onChange, featureProperties }: FilterEditorProps) {
  const [mode, setMode] = useState<"visual" | "json">(
    canParseFilter(value) ? "visual" : "json",
  );

  return (
    <div className="rounded-radius-sm border border-border-subtle bg-surface-sunken">
      <div className="flex items-center border-b border-border-subtle px-2 py-1">
        <span className="mr-auto text-[9px] font-medium text-text-muted">Filter</span>
        <div className="flex gap-0.5">
          <button type="button" onClick={() => setMode("visual")} className={`px-2 py-0.5 text-[9px] font-medium rounded-radius-sm ${mode === "visual" ? "bg-accent text-accent-text" : "text-text-muted"}`}>Visual</button>
          <button type="button" onClick={() => setMode("json")} className={`px-2 py-0.5 text-[9px] font-medium rounded-radius-sm ${mode === "json" ? "bg-accent text-accent-text" : "text-text-muted"}`}>JSON</button>
        </div>
      </div>
      <div className="p-2">
        {mode === "json" ? (
          <FilterJsonMode value={value} onChange={onChange} />
        ) : (
          <FilterVisualMode value={value} onChange={onChange} featureProperties={featureProperties} />
        )}
      </div>
    </div>
  );
}

function FilterJsonMode({ value, onChange }: { value: unknown; onChange: (v: unknown) => void }) {
  const [text, setText] = useState(value ? JSON.stringify(value, null, 2) : "");
  const [error, setError] = useState("");

  const handleBlur = () => {
    if (!text.trim()) { onChange(undefined); setError(""); return; }
    try { onChange(JSON.parse(text)); setError(""); } catch (e) { setError(e instanceof Error ? e.message : "Invalid JSON"); }
  };

  return (
    <div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} onBlur={handleBlur} rows={4} className="w-full rounded-radius-sm border border-border-subtle bg-surface-base p-2 font-mono text-[10px] text-text-primary focus:border-accent focus:outline-none" spellCheck={false} placeholder='["all", ["==", ["get", "type"], "water"]]' />
      {error && <div className="mt-1 text-[9px] text-error">{error}</div>}
    </div>
  );
}

function FilterVisualMode({ value, onChange, featureProperties }: { value: unknown; onChange: (v: unknown) => void; featureProperties?: FeaturePropertyInfo }) {
  const { combiner, rows } = parseFilter(value);
  const propNames = featureProperties?.names ?? [];
  const propValues = featureProperties?.values ?? {};

  const rebuild = (newCombiner: string, newRows: FilterRow[]) => {
    if (newRows.length === 0) { onChange(undefined); return; }
    const expressions = newRows.map((r) => {
      if (r.operator === "has") return ["has", r.property];
      if (r.operator === "!has") return ["!", ["has", r.property]];
      const val = isNaN(Number(r.value)) ? r.value : Number(r.value);
      return [r.operator, ["get", r.property], val];
    });
    if (expressions.length === 1) { onChange(expressions[0]); return; }
    onChange([newCombiner, ...expressions]);
  };

  const updateRow = (idx: number, field: keyof FilterRow, val: string) => {
    const newRows = [...rows];
    newRows[idx] = { ...newRows[idx]!, [field]: val };
    rebuild(combiner, newRows);
  };

  const addRow = () => {
    rebuild(combiner, [...rows, { property: propNames[0] ?? "", operator: "==", value: "" }]);
  };

  const removeRow = (idx: number) => {
    rebuild(combiner, rows.filter((_, i) => i !== idx));
  };

  return (
    <div>
      {rows.length > 1 && (
        <div className="mb-2 flex items-center gap-1.5">
          <span className="text-[9px] text-text-muted">Combine with:</span>
          <select value={combiner} onChange={(e) => rebuild(e.target.value, rows)} className="h-5 rounded-radius-sm border border-border-subtle bg-surface-base px-1 text-[9px] text-text-primary">
            <option value="all">All (AND)</option>
            <option value="any">Any (OR)</option>
          </select>
        </div>
      )}

      {rows.map((row, i) => (
        <div key={i} className="flex items-center gap-1 py-0.5">
          {/* Property — dropdown if we have names, text input otherwise */}
          {propNames.length > 0 ? (
            <select
              value={row.property}
              onChange={(e) => updateRow(i, "property", e.target.value)}
              className="h-5 w-24 rounded-radius-sm border border-border-subtle bg-surface-base px-1 text-[9px] text-text-primary"
            >
              <option value="">property...</option>
              {propNames.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          ) : (
            <input type="text" value={row.property} onChange={(e) => updateRow(i, "property", e.target.value)} placeholder="property" className="h-5 w-20 rounded-radius-sm border border-border-subtle bg-surface-base px-1 text-[9px] text-text-primary" />
          )}

          {/* Operator */}
          <select value={row.operator} onChange={(e) => updateRow(i, "operator", e.target.value)} className="h-5 rounded-radius-sm border border-border-subtle bg-surface-base px-1 text-[9px] text-text-primary">
            {OPERATORS.map((op) => <option key={op.value} value={op.value}>{op.label}</option>)}
          </select>

          {/* Value — dropdown if we have values for this property, text input otherwise */}
          {row.operator !== "has" && row.operator !== "!has" && (
            propValues[row.property] && (propValues[row.property]?.length ?? 0) > 0 ? (
              <select
                value={row.value}
                onChange={(e) => updateRow(i, "value", e.target.value)}
                className="h-5 flex-1 rounded-radius-sm border border-border-subtle bg-surface-base px-1 text-[9px] text-text-primary"
              >
                <option value="">value...</option>
                {propValues[row.property]?.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            ) : (
              <input type="text" value={row.value} onChange={(e) => updateRow(i, "value", e.target.value)} placeholder="value" className="h-5 flex-1 rounded-radius-sm border border-border-subtle bg-surface-base px-1 text-[9px] text-text-primary" />
            )
          )}

          <button type="button" onClick={() => removeRow(i)} className="text-[9px] text-text-muted hover:text-error">✕</button>
        </div>
      ))}

      {propNames.length > 0 && (
        <div className="mt-1 text-[8px] text-text-muted">
          {propNames.length} properties from loaded features
        </div>
      )}

      <button type="button" onClick={addRow} className="mt-1 text-[9px] text-accent hover:text-accent-hover">+ Add condition</button>
    </div>
  );
}

// ── Helpers ──

function canParseFilter(value: unknown): boolean {
  if (!value) return true;
  if (!Array.isArray(value)) return false;
  const op = value[0];
  return typeof op === "string";
}

function parseFilter(value: unknown): { combiner: string; rows: FilterRow[] } {
  if (!value || !Array.isArray(value)) return { combiner: "all", rows: [] };

  const op = value[0] as string;

  if (op === "all" || op === "any") {
    const rows = (value.slice(1) as unknown[]).map(parseOneCondition).filter(Boolean) as FilterRow[];
    return { combiner: op, rows };
  }

  const single = parseOneCondition(value);
  return { combiner: "all", rows: single ? [single] : [] };
}

function parseOneCondition(expr: unknown): FilterRow | null {
  if (!Array.isArray(expr)) return null;
  const op = expr[0] as string;

  if (op === "has") return { property: expr[1] as string, operator: "has", value: "" };
  if (op === "!" && Array.isArray(expr[1]) && expr[1][0] === "has") {
    return { property: expr[1][1] as string, operator: "!has", value: "" };
  }

  if (["==", "!=", ">", "<", ">=", "<="].includes(op)) {
    const left = expr[1];
    const right = expr[2];
    const property = Array.isArray(left) && left[0] === "get" ? (left[1] as string) : String(left);
    return { property, operator: op, value: String(right) };
  }

  return null;
}
